import { useParams } from "react-router";

import { useGetDealReportingMetrics } from "@vega-api/generated/coinvest-deal-report/coinvest-deal-report";
import { useGetDealReportingFinancialReturns } from "@vega-api/generated/coinvest-deal-reporting-financial-returns/coinvest-deal-reporting-financial-returns";
import { useGetDealGeneralPartnerViewV3 } from "@vega-api/generated/deal/deal";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";

import { CommittedOrganisationsSection } from "./components/CommittedOrganisationsSection";
import { DealCancelledBanner } from "./components/DealCancelledBanner";
import { DealOutcomeMetrics } from "./components/DealOutcomeMetrics";
import { DealStillActiveBanner } from "./components/DealStillActiveBanner";
import { FinancialReturnsSection } from "./components/FinancialReturnsSection";
import { QuarterlyDataSection } from "./components/QuarterlyDataSection";
import { ReportingHistorySection } from "./components/ReportingHistorySection";

export const ReportPage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  const { id: dealId = "" } = useParams<{ id: string }>();

  const { data: dealData, isLoading: isDealLoading } =
    useGetDealGeneralPartnerViewV3(dealId, {
      query: {
        enabled: !!dealId,
        select: (data) => data.data?.deal,
      },
    });

  const isClosed = dealData?.status === DealStatusEnum.CLOSED;
  const isCancelled = dealData?.status === DealStatusEnum.CANCELLED;

  const {
    data: reportingMetrics,
    isError: isMetricsError,
    isPending: isMetricsPending,
    refetch: refetchMetrics,
  } = useGetDealReportingMetrics(dealId, {
    query: {
      enabled: !!dealId && isClosed,
      select: (data) => data.data,
    },
  });

  const {
    data: financialReturns,
    isError: isFinancialReturnsError,
    isPending: isFinancialReturnsPending,
    refetch: refetchFinancialReturns,
  } = useGetDealReportingFinancialReturns(dealId, undefined, {
    query: {
      enabled: !!dealId && isClosed,
      select: (data) => data.data,
    },
  });

  if (!isClosed) {
    return (
      <div className="mt-1">
        <CoInvestmentPageHeader
          fundName={dealData?.name ?? ""}
          isLoading={isDealLoading}
          title={m.coInvestments_nav_report()}
        />
        <div className="mt-4">
          {isCancelled ? (
            <DealCancelledBanner />
          ) : (
            <DealStillActiveBanner closingDate={dealData?.closingDate} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1 pb-52">
      <CoInvestmentPageHeader
        fundName={dealData.name}
        isLoading={isDealLoading}
        title={m.coInvestments_nav_report()}
      />
      <div className="mt-6 flex flex-col gap-8">
        <DealOutcomeMetrics
          isError={isMetricsError}
          isLoading={isMetricsPending}
          metrics={reportingMetrics?.dealOutcome ?? []}
          onRetry={refetchMetrics}
        />

        <FinancialReturnsSection
          data={financialReturns}
          isError={isFinancialReturnsError}
          isPending={isFinancialReturnsPending}
          onRetry={refetchFinancialReturns}
        />

        <CommittedOrganisationsSection
          committedLps={reportingMetrics?.committedLps ?? []}
          isError={isMetricsError}
          isLoading={isMetricsPending}
          onRetry={refetchMetrics}
        />

        <QuarterlyDataSection dealId={dealId} />

        <ReportingHistorySection
          data={financialReturns}
          editContext={{ dealId }}
          isError={isFinancialReturnsError}
          isPending={isFinancialReturnsPending}
          onRetry={refetchFinancialReturns}
        />
      </div>
    </div>
  );
};
